import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { Show, createMemo, createSignal, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import {
    BUILDING_TYPE,
    type BuildingInput,
    DAYTIME_CONSUMPTION,
    METER_BOX,
    ROOF_MATERIAL,
    type VendeeInput
} from '~/lib/graphql/types';
import { trackEvent, trackSubmitSimulation } from '~/lib/helpers/analytics';
import { encodeOffer } from '~/lib/helpers/offer-url';
import { posthog } from '~/lib/helpers/posthog';
import { countryCode } from '~/lib/i18n';
import { getOfferFn } from '~/lib/server/graphql';
import { useCalculator } from '~/lib/store';
import type { Contact } from '~/lib/store/types';

export const Route = createFileRoute('/$locale/contact')({
    component: ContactPage
});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
    // Simple check: at least 8 digits
    return phone.replace(/\D/g, '').length >= 8;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ContactPage() {
    const [state, actions] = useCalculator();
    const navigate = useNavigate();

    // Form state
    const [firstName, setFirstName] = createSignal('');
    const [lastName, setLastName] = createSignal('');
    const [email, setEmail] = createSignal('');
    const [phone, setPhone] = createSignal('');
    const [company, setCompany] = createSignal('');
    const [jobTitle, setJobTitle] = createSignal('');
    const [referralCode, setReferralCode] = createSignal('');
    const [errors, setErrors] = createSignal<Record<string, string>>({});
    const [submitting, setSubmitting] = createSignal(false);

    onMount(() => {
        posthog.capture('$pageview');

        if (!state.quote.address || !state.quote.type) {
            navigate({ to: '/$locale', search: (prev) => prev });
        }

        if (state.referralCode) {
            setReferralCode(state.referralCode);
        }
    });

    const phonePrefix = createMemo(() => {
        return countryCode() === 'SG' ? '+65' : '+60';
    });

    const isCommercial = createMemo(
        () =>
            state.quote.type === BUILDING_TYPE.OWNED_COMMERCIAL_PROPERTY ||
            state.quote.type === BUILDING_TYPE.RENTED_COMMERCIAL_PROPERTY
    );

    function validate(): boolean {
        const errs: Record<string, string> = {};

        if (!firstName().trim()) errs.firstName = 'First name is required';
        else if (firstName().trim().length > 100)
            errs.firstName = 'First name must be 100 characters or less';
        if (!lastName().trim()) errs.lastName = 'Last name is required';
        else if (lastName().trim().length > 100)
            errs.lastName = 'Last name must be 100 characters or less';
        if (!email().trim()) {
            errs.email = 'Email is required';
        } else if (!isValidEmail(email())) {
            errs.email = 'Please enter a valid email';
        }
        if (!phone().trim()) {
            errs.phone = 'Phone number is required';
        } else if (!isValidPhone(phone())) {
            errs.phone = 'Please enter a valid phone number';
        }
        const trimmedReferral = referralCode().trim();
        if (trimmedReferral && trimmedReferral.length < 2) {
            errs.referral = 'Must be at least 2 characters';
        } else if (trimmedReferral.length > 50) {
            errs.referral = 'Must be 50 characters or less';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    }

    async function handleSubmit(e: SubmitEvent) {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        actions.setLoading('confirmGetOffer', true);
        actions.setLoadingPercentage(0);

        // Simulate progress
        const progressInterval = setInterval(() => {
            actions.setLoadingPercentage(
                Math.min(state.loadingPercentage + Math.random() * 15, 90)
            );
        }, 500);

        try {
            const trimmedFirstName = firstName().trim();
            const trimmedLastName = lastName().trim();
            const trimmedEmail = email().trim();
            const trimmedPhone = phone().trim();
            const trimmedCompany = company().trim();
            const trimmedJobTitle = jobTitle().trim();
            const trimmedReferral = referralCode().trim();

            const contact: Contact = {
                name: trimmedFirstName,
                family_name: trimmedLastName,
                email: trimmedEmail,
                phone: `${phonePrefix()}${trimmedPhone}`,
                company: trimmedCompany,
                job_title: trimmedJobTitle,
                tandc: true,
                referral: trimmedReferral
            };

            // Store contact in quote
            actions.setQuote('contact', contact as never);

            trackEvent('Step: contact', {
                quote: { ...state.quote, contact },
                set: state.set,
                direction: 'continue',
                utm: state.utm
            });

            const isCommercialType =
                state.quote.type === BUILDING_TYPE.OWNED_COMMERCIAL_PROPERTY ||
                state.quote.type === BUILDING_TYPE.RENTED_COMMERCIAL_PROPERTY;

            const building: BuildingInput = {
                type: state.quote.type!,
                lng: Number(state.quote.lng) || 0,
                lat: Number(state.quote.lat) || 0,
                area: Number(state.quote.area) || 0,
                polygon: state.quote.polygon ?? [{ lat: 0, lng: 0 }],
                daytime_consumption:
                    (state.quote.dayTimeConsumption as DAYTIME_CONSUMPTION) ??
                    DAYTIME_CONSUMPTION.MORNING_AND_NIGHT,
                material:
                    (state.quote.roof as ROOF_MATERIAL) ?? ROOF_MATERIAL.OTHER,
                storeys: state.quote.storeys ? Number(state.quote.storeys) : 1,
                utilities: state.quote.utilities
                    ? Number(state.quote.utilities)
                    : 250,
                retailer:
                    state.quote.retailer ??
                    (countryCode() === 'SG' ? 'SPGROUP' : undefined),
                meter_box:
                    (state.quote.meter_box as METER_BOX) ??
                    (isCommercialType
                        ? METER_BOX.THREE_PHASE
                        : METER_BOX.SINGLE_PHASE),
                energy: state.quote.energy ?? null,
                height: state.quote.height ?? null
            };

            const vendeeInput: VendeeInput = {
                name: trimmedFirstName,
                family_name: trimmedLastName,
                email: trimmedEmail,
                phone: `${phonePrefix()}${trimmedPhone}`,
                company: trimmedCompany || '',
                job_title: trimmedJobTitle || '',
                timeframe: '1',
                referral: trimmedReferral,
                address: state.quote.address ?? '',
                address_components: state.quote.address_components ?? [],
                building,
                utm: state.utm
            };

            const result = await getOfferFn({
                data: {
                    country_code: countryCode(),
                    input: vendeeInput
                }
            });

            if (!result.success) {
                throw new Error(result.error);
            }

            clearInterval(progressInterval);
            actions.setLoadingPercentage(100);
            actions.setOffer(result.data);
            actions.setLoading('confirmGetOffer', false);
            setSubmitting(false);

            if (result.data.code && trimmedEmail && trimmedPhone) {
                trackSubmitSimulation({
                    leadId: result.data.code,
                    email: trimmedEmail,
                    phone: `${phonePrefix()}${trimmedPhone}`,
                    type: state.quote.type ?? null
                });
            }

            const encoded = encodeOffer(result.data);
            navigate({
                to: '/$locale/offer',
                hash: encoded,
                search: (prev) => prev
            });
        } catch (err) {
            clearInterval(progressInterval);
            actions.setLoading('confirmGetOffer', false);
            setSubmitting(false);
            const message =
                err instanceof Error ? err.message : 'Something went wrong';
            actions.setError({ message });
        }
    }

    return (
        <div class="min-h-screen bg-white">
            <Header />

            {/* Desktop Stepper */}
            <div class="hidden md:flex w-full py-4 px-10 h-16 items-center border-b border-gray-100">
                <div class="w-1/4" />
                <div class="flex justify-center w-2/4 text-sm text-gray-500 font-medium">
                    Almost there — Enter your details
                </div>
                <div class="w-1/4" />
            </div>

            {/* Loading overlay */}
            <Show when={state.loading.confirmGetOffer}>
                <div class="fixed inset-0 z-50 bg-white/90 flex flex-col items-center justify-center">
                    <div class="w-64">
                        <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
                            <span>Generating your quote...</span>
                            <span>{Math.round(state.loadingPercentage)}%</span>
                        </div>
                        <div class="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                class="h-full bg-brand-blue rounded-full transition-all duration-300"
                                style={{ width: `${state.loadingPercentage}%` }}
                            />
                        </div>
                        <p class="text-xs text-gray-400 mt-3 text-center">
                            This may take a moment...
                        </p>
                    </div>
                </div>
            </Show>

            {/* Error message */}
            <Show when={state.error}>
                <div class="max-w-xl mx-auto mt-4 px-4">
                    <div class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                        <svg
                            class="w-5 h-5 text-red-500 shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <div>
                            <p class="text-sm text-red-800 font-medium">
                                {state.error!.title ?? 'Error'}
                            </p>
                            <p class="text-sm text-red-600 mt-0.5">
                                {state.error!.message}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="ml-auto text-red-400 hover:text-red-600"
                            onClick={() => actions.clearError()}>
                            <svg
                                class="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </Show>

            {/* Form */}
            <div class="max-w-xl mx-auto px-4 py-8 sm:py-12">
                <h1 class="text-2xl sm:text-3xl font-heading font-bold text-brand-black text-center">
                    Get Your Detailed Quote
                </h1>
                <p class="mt-2 text-sm text-gray-500 text-center">
                    Enter your contact details to receive your personalized
                    solar quote.
                </p>

                <form onSubmit={handleSubmit} class="mt-8 space-y-5">
                    {/* Name row */}
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 mb-1.5"
                                for="firstName">
                                First Name *
                            </label>
                            <input
                                id="firstName"
                                type="text"
                                maxLength={100}
                                value={firstName()}
                                onInput={(e) =>
                                    setFirstName(e.currentTarget.value)
                                }
                                onBlur={() => setFirstName(firstName().trim())}
                                class={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent ${
                                    errors().firstName
                                        ? 'border-red-400'
                                        : 'border-gray-300'
                                }`}
                                placeholder="John"
                            />
                            <Show when={errors().firstName}>
                                <p class="text-xs text-red-500 mt-1">
                                    {errors().firstName}
                                </p>
                            </Show>
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 mb-1.5"
                                for="lastName">
                                Last Name *
                            </label>
                            <input
                                id="lastName"
                                type="text"
                                maxLength={100}
                                value={lastName()}
                                onInput={(e) =>
                                    setLastName(e.currentTarget.value)
                                }
                                onBlur={() => setLastName(lastName().trim())}
                                class={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent ${
                                    errors().lastName
                                        ? 'border-red-400'
                                        : 'border-gray-300'
                                }`}
                                placeholder="Doe"
                            />
                            <Show when={errors().lastName}>
                                <p class="text-xs text-red-500 mt-1">
                                    {errors().lastName}
                                </p>
                            </Show>
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 mb-1.5"
                            for="email">
                            Email *
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email()}
                            onInput={(e) => setEmail(e.currentTarget.value)}
                            onBlur={() => setEmail(email().trim())}
                            class={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent ${
                                errors().email
                                    ? 'border-red-400'
                                    : 'border-gray-300'
                            }`}
                            placeholder="john@example.com"
                        />
                        <Show when={errors().email}>
                            <p class="text-xs text-red-500 mt-1">
                                {errors().email}
                            </p>
                        </Show>
                    </div>

                    {/* Phone */}
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 mb-1.5"
                            for="phone">
                            Phone Number *
                        </label>
                        <div class="flex gap-2">
                            <div class="flex items-center px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600">
                                {phonePrefix()}
                            </div>
                            <input
                                id="phone"
                                type="tel"
                                value={phone()}
                                onInput={(e) => setPhone(e.currentTarget.value)}
                                onBlur={() => setPhone(phone().trim())}
                                class={`flex-1 px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent ${
                                    errors().phone
                                        ? 'border-red-400'
                                        : 'border-gray-300'
                                }`}
                                placeholder="91234567"
                            />
                        </div>
                        <Show when={errors().phone}>
                            <p class="text-xs text-red-500 mt-1">
                                {errors().phone}
                            </p>
                        </Show>
                    </div>

                    {/* Referral code */}
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 mb-1.5"
                            for="referralCode">
                            Referral Code (Optional)
                        </label>
                        <input
                            id="referralCode"
                            type="text"
                            maxLength={50}
                            value={referralCode()}
                            onInput={(e) =>
                                setReferralCode(e.currentTarget.value)
                            }
                            onBlur={() =>
                                setReferralCode(referralCode().trim())
                            }
                            class={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent ${
                                errors().referral
                                    ? 'border-red-400'
                                    : 'border-gray-300'
                            }`}
                            placeholder="Referral Code"
                        />
                        <Show when={errors().referral}>
                            <p class="text-xs text-red-500 mt-1">
                                {errors().referral}
                            </p>
                        </Show>
                    </div>

                    {/* Commercial-only fields */}
                    <Show when={isCommercial()}>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 mb-1.5"
                                    for="company">
                                    Company Name
                                </label>
                                <input
                                    id="company"
                                    type="text"
                                    value={company()}
                                    onInput={(e) =>
                                        setCompany(e.currentTarget.value)
                                    }
                                    onBlur={() => setCompany(company().trim())}
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                                    placeholder="Company name"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 mb-1.5"
                                    for="jobTitle">
                                    Job Title
                                </label>
                                <input
                                    id="jobTitle"
                                    type="text"
                                    value={jobTitle()}
                                    onInput={(e) =>
                                        setJobTitle(e.currentTarget.value)
                                    }
                                    onBlur={() =>
                                        setJobTitle(jobTitle().trim())
                                    }
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                                    placeholder="Job title"
                                />
                            </div>
                        </div>
                    </Show>

                    {/* Submit */}
                    <div class="pt-4">
                        <button
                            type="submit"
                            disabled={submitting()}
                            class="w-full py-3.5 rounded-full bg-brand-yellow text-brand-black font-semibold text-base transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90">
                            <Show when={submitting()} fallback="Get My Quote">
                                <span class="flex items-center justify-center gap-2">
                                    <svg
                                        class="animate-spin h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            class="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            stroke-width="4"
                                        />
                                        <path
                                            class="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                    Generating...
                                </span>
                            </Show>
                        </button>
                    </div>

                    <p class="text-xs text-gray-400 text-center">
                        By submitting, you agree to our Terms of Service and
                        Privacy Policy.
                    </p>
                </form>
            </div>
        </div>
    );
}
